import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Camera, CheckCircle, XCircle, Zap, X, FileCheck, FileX } from 'lucide-react';
import API_BASE_URL from '../config/api';
import {
  ArucoMarker,
  detectAllArucoMarkers,
  validateDetectedMarkers,
} from '../utils/arucoDecoder';

interface ScannedForm {
  id: string;
  imageBlob: Blob;
  imageThumbnail: string;
  timestamp: Date;
  uploadStatus: 'pending' | 'uploaded' | 'error';
}

const CameraScannerPage: React.FC = () => {
  const navigate = useNavigate();
  const { testId } = useParams<{ testId: string }>();

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [scanning, setScanning] = useState<boolean>(false);
  const [scannedForms, setScannedForms] = useState<ScannedForm[]>([]);
  const [isCapturing, setIsCapturing] = useState<boolean>(false);
  const [autoScanEnabled, setAutoScanEnabled] = useState<boolean>(false);
  const [showThumbnails, setShowThumbnails] = useState<boolean>(false);
  const [cameraError, setCameraError] = useState<string>('');
  const [videoReady, setVideoReady] = useState<boolean>(false);
  const [formDetected, setFormDetected] = useState<boolean>(false);
  const [detectedMarkers, setDetectedMarkers] = useState<{
    topLeft: ArucoMarker | null;
    topRight: ArucoMarker | null;
    bottomLeft: ArucoMarker | null;
    bottomRight: ArucoMarker | null;
    isValid: boolean;
  }>({
    topLeft: null,
    topRight: null,
    bottomLeft: null,
    bottomRight: null,
    isValid: false,
  });

  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const autoScanIntervalRef = useRef<number | null>(null);
  const detectionIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // Effet pour la détection continue des marqueurs
  useEffect(() => {
    if (videoReady) {
      detectionIntervalRef.current = window.setInterval(() => {
        detectFormMarkers();
      }, 200); // Vérifier 5 fois par seconde
    }
    return () => {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
        detectionIntervalRef.current = null;
      }
    };
  }, [videoReady]);

  // Effet pour redessiner l'overlay quand les marqueurs changent
  useEffect(() => {
    if (videoReady) {
      drawOverlay();
    }
  }, [videoReady, detectedMarkers]);

  const startCamera = async () => {
    setCameraError('');
    setVideoReady(false);

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });

      setStream(mediaStream);
      setScanning(true);

      // Attendre que le DOM soit mis à jour avant d'assigner le stream
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.onloadedmetadata = () => {
            setVideoReady(true);
          };
        }
      }, 100);

    } catch (err: any) {
      console.error('Erreur accès caméra:', err);
      setCameraError(err.message || 'Impossible d\'accéder à la caméra');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setScanning(false);
    setVideoReady(false);
    setFormDetected(false);
    stopAutoScan();
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }
  };

  const startAutoScan = () => {
    setAutoScanEnabled(true);

    autoScanIntervalRef.current = window.setInterval(() => {
      if (!isCapturing) {
        handleManualCapture();
      }
    }, 3000);
  };

  const stopAutoScan = () => {
    setAutoScanEnabled(false);
    if (autoScanIntervalRef.current) {
      clearInterval(autoScanIntervalRef.current);
      autoScanIntervalRef.current = null;
    }
  };

  const vibrate = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(200);
    }
  };

  // Fonction de détection des marqueurs ArUco de formulaire (nouveau système avec décodage d'ID)
  const detectFormMarkers = () => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) return;

    try {
      // Créer un canvas temporaire pour l'analyse
      const canvas = document.createElement('canvas');
      const scale = 0.25; // Downscale pour performance
      canvas.width = video.videoWidth * scale;
      canvas.height = video.videoHeight * scale;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Dessiner la vidéo dans le canvas
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      console.log('🔍 Début de la détection...', {
        canvasSize: `${canvas.width}x${canvas.height}`,
        scale: scale
      });

      // Détecter tous les marqueurs dans l'image
      const startTime = performance.now();
      const allMarkers = detectAllArucoMarkers(imageData);
      const detectionTime = performance.now() - startTime;

      console.log(`🎯 Marqueurs bruts détectés: ${allMarkers.length}`, {
        markers: allMarkers.map(m => `ID=${m.id}, conf=${m.confidence.toFixed(2)}`),
        temps: `${detectionTime.toFixed(0)}ms`
      });

      // Convertir les coordonnées du canvas 25% vers les coordonnées vidéo 100%
      const scaledMarkers = allMarkers.map(marker => ({
        ...marker,
        centerX: marker.centerX / scale,
        centerY: marker.centerY / scale,
        corners: marker.corners.map(corner => ({
          x: corner.x / scale,
          y: corner.y / scale,
        })),
      }));

      // Valider et organiser les marqueurs par ID
      const validated = validateDetectedMarkers(scaledMarkers);

      console.log('✨ Validation:', {
        TL: validated.topLeft?.id,
        TR: validated.topRight?.id,
        BL: validated.bottomLeft?.id,
        BR: validated.bottomRight?.id,
        isValid: validated.isValid
      });

      // Mettre à jour l'état
      setFormDetected(validated.isValid);
      setDetectedMarkers(validated);

      // Logs de diagnostic
      if (validated.isValid) {
        console.log('✅ Formulaire détecté avec succès!', {
          TL: validated.topLeft ? `ID=${validated.topLeft.id}, conf=${validated.topLeft.confidence.toFixed(2)}` : 'non détecté',
          TR: validated.topRight ? `ID=${validated.topRight.id}, conf=${validated.topRight.confidence.toFixed(2)}` : 'non détecté',
          BL: validated.bottomLeft ? `ID=${validated.bottomLeft.id}, conf=${validated.bottomLeft.confidence.toFixed(2)}` : 'non détecté',
          BR: validated.bottomRight ? `ID=${validated.bottomRight.id}, conf=${validated.bottomRight.confidence.toFixed(2)}` : 'non détecté',
        });
      }
    } catch (error) {
      console.error('❌ Erreur lors de la détection:', error);
    }
  };

  // Fonction pour dessiner l'overlay (marqueurs ArUco détectés)
  const drawOverlay = () => {
    const video = videoRef.current;
    const overlayCanvas = overlayCanvasRef.current;
    if (!video || video.videoWidth === 0 || !overlayCanvas) return;

    const overlayCtx = overlayCanvas.getContext('2d');
    if (!overlayCtx) return;

    // Obtenir les dimensions d'affichage réelles de la vidéo (avec object-cover)
    const displayWidth = video.clientWidth;
    const displayHeight = video.clientHeight;
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    // Calculer la transformation object-cover
    const videoRatio = videoWidth / videoHeight;
    const displayRatio = displayWidth / displayHeight;

    let scaleX, scaleY, offsetX = 0, offsetY = 0;

    if (videoRatio > displayRatio) {
      // Vidéo plus large : recadrée sur les côtés
      scaleY = displayHeight / videoHeight;
      scaleX = scaleY;
      offsetX = (displayWidth - (videoWidth * scaleX)) / 2;
    } else {
      // Vidéo plus haute : recadrée en haut/bas
      scaleX = displayWidth / videoWidth;
      scaleY = scaleX;
      offsetY = (displayHeight - (videoHeight * scaleY)) / 2;
    }

    // Ajuster la taille du canvas overlay à celle de l'affichage
    if (overlayCanvas.width !== displayWidth || overlayCanvas.height !== displayHeight) {
      overlayCanvas.width = displayWidth;
      overlayCanvas.height = displayHeight;
    }

    // Effacer l'overlay
    overlayCtx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);

    // Dessiner UNIQUEMENT les marqueurs détectés (style réalité augmentée)
    const markerList = [
      { marker: detectedMarkers.topLeft, label: 'TL' },
      { marker: detectedMarkers.topRight, label: 'TR' },
      { marker: detectedMarkers.bottomLeft, label: 'BL' },
      { marker: detectedMarkers.bottomRight, label: 'BR' },
    ];

    markerList.forEach(({ marker, label }) => {
      // Ne dessiner que si le marqueur est détecté
      if (!marker) return;

      // Transformer les coordonnées de la vidéo native vers l'affichage avec object-cover
      const displayX = marker.centerX * scaleX + offsetX;
      const displayY = marker.centerY * scaleY + offsetY;

      const markerSize = Math.min(displayWidth, displayHeight) * 0.15;

      // Style réalité augmentée : cadre vert néon
      overlayCtx.strokeStyle = '#22c55e';
      overlayCtx.fillStyle = '#22c55e';
      overlayCtx.lineWidth = 4;
      overlayCtx.globalAlpha = 1.0;

      // Dessiner un cadre avec coins stylisés (style AR)
      const cornerLength = markerSize * 0.2;
      const x1 = displayX - markerSize / 2;
      const y1 = displayY - markerSize / 2;
      const x2 = displayX + markerSize / 2;
      const y2 = displayY + markerSize / 2;

      // Coins haut-gauche
      overlayCtx.beginPath();
      overlayCtx.moveTo(x1 + cornerLength, y1);
      overlayCtx.lineTo(x1, y1);
      overlayCtx.lineTo(x1, y1 + cornerLength);
      overlayCtx.stroke();

      // Coins haut-droite
      overlayCtx.beginPath();
      overlayCtx.moveTo(x2 - cornerLength, y1);
      overlayCtx.lineTo(x2, y1);
      overlayCtx.lineTo(x2, y1 + cornerLength);
      overlayCtx.stroke();

      // Coins bas-gauche
      overlayCtx.beginPath();
      overlayCtx.moveTo(x1, y2 - cornerLength);
      overlayCtx.lineTo(x1, y2);
      overlayCtx.lineTo(x1 + cornerLength, y2);
      overlayCtx.stroke();

      // Coins bas-droite
      overlayCtx.beginPath();
      overlayCtx.moveTo(x2 - cornerLength, y2);
      overlayCtx.lineTo(x2, y2);
      overlayCtx.lineTo(x2, y2 - cornerLength);
      overlayCtx.stroke();

      // Point central avec effet de pulsation
      overlayCtx.beginPath();
      overlayCtx.arc(displayX, displayY, 6, 0, 2 * Math.PI);
      overlayCtx.fill();

      // Halo autour du point central
      overlayCtx.globalAlpha = 0.3;
      overlayCtx.beginPath();
      overlayCtx.arc(displayX, displayY, 12, 0, 2 * Math.PI);
      overlayCtx.fill();

      // Label du marqueur avec fond semi-transparent
      overlayCtx.globalAlpha = 0.8;
      overlayCtx.fillStyle = '#22c55e';
      overlayCtx.font = 'bold 14px monospace';
      const labelText = `${label} (ID:${marker.id}, ${(marker.confidence * 100).toFixed(0)}%)`;
      const textMetrics = overlayCtx.measureText(labelText);
      const textX = displayX - textMetrics.width / 2;
      const textY = displayY - markerSize / 2 - 15;

      // Fond du label
      overlayCtx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      overlayCtx.fillRect(textX - 4, textY - 14, textMetrics.width + 8, 18);

      // Texte du label
      overlayCtx.fillStyle = '#22c55e';
      overlayCtx.fillText(labelText, textX, textY);
    });

    // Dessiner le quadrilatère formé par les 4 marqueurs (si tous détectés)
    if (detectedMarkers.isValid && detectedMarkers.topLeft && detectedMarkers.topRight && detectedMarkers.bottomLeft && detectedMarkers.bottomRight) {
      const tl = {
        x: detectedMarkers.topLeft.centerX * scaleX + offsetX,
        y: detectedMarkers.topLeft.centerY * scaleY + offsetY,
      };
      const tr = {
        x: detectedMarkers.topRight.centerX * scaleX + offsetX,
        y: detectedMarkers.topRight.centerY * scaleY + offsetY,
      };
      const bl = {
        x: detectedMarkers.bottomLeft.centerX * scaleX + offsetX,
        y: detectedMarkers.bottomLeft.centerY * scaleY + offsetY,
      };
      const br = {
        x: detectedMarkers.bottomRight.centerX * scaleX + offsetX,
        y: detectedMarkers.bottomRight.centerY * scaleY + offsetY,
      };

      // Tracer le quadrilatère
      overlayCtx.strokeStyle = '#22c55e';
      overlayCtx.lineWidth = 3;
      overlayCtx.globalAlpha = 0.6;
      overlayCtx.beginPath();
      overlayCtx.moveTo(tl.x, tl.y);
      overlayCtx.lineTo(tr.x, tr.y);
      overlayCtx.lineTo(br.x, br.y);
      overlayCtx.lineTo(bl.x, bl.y);
      overlayCtx.closePath();
      overlayCtx.stroke();

      // Remplir avec une couleur semi-transparente
      overlayCtx.fillStyle = 'rgba(34, 197, 94, 0.1)';
      overlayCtx.fill();
    }

    overlayCtx.globalAlpha = 1.0;
  };


  const captureFrame = async (): Promise<Blob | null> => {
    const video = videoRef.current;
    const canvas = document.createElement('canvas');

    if (!video) return null;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(video, 0, 0);

    return new Promise(resolve => {
      canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.85);
    });
  };

  const createThumbnail = async (blob: Blob): Promise<string> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxSize = 200;
          const ratio = Math.min(maxSize / img.width, maxSize / img.height);

          canvas.width = img.width * ratio;
          canvas.height = img.height * ratio;

          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

          resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(blob);
    });
  };

  const uploadAndSave = async (scan: ScannedForm) => {
    const token = localStorage.getItem('authToken');
    const formData = new FormData();

    formData.append('photo', scan.imageBlob);
    formData.append('testId', testId!);

    try {
      const response = await fetch(`${API_BASE_URL}/api/photo-ocr/quick-scan`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();

      setScannedForms(prev => prev.map(s =>
        s.id === scan.id ? { ...s, uploadStatus: 'uploaded' } : s
      ));

      console.log('Scan enregistré:', data.scanId);

    } catch (err) {
      console.error('Erreur upload:', err);
      setScannedForms(prev => prev.map(s =>
        s.id === scan.id ? { ...s, uploadStatus: 'error' } : s
      ));
    }
  };

  const handleManualCapture = async () => {
    if (isCapturing) return;

    setIsCapturing(true);

    vibrate();

    const imageBlob = await captureFrame();

    if (imageBlob) {
      const thumbnail = await createThumbnail(imageBlob);

      const newScan: ScannedForm = {
        id: crypto.randomUUID(),
        imageBlob,
        imageThumbnail: thumbnail,
        timestamp: new Date(),
        uploadStatus: 'pending'
      };

      setScannedForms(prev => [...prev, newScan]);

      await uploadAndSave(newScan);
    }

    setTimeout(() => {
      setIsCapturing(false);
    }, 500);
  };

  const handleExit = () => {
    stopCamera();
    navigate(`/test-results/${testId}`);
  };

  // Mode plein écran quand la caméra est active
  if (scanning) {
    return (
      <div className="fixed inset-0 bg-black z-50">
        {/* Indicateur de chargement */}
        {!videoReady && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p>Chargement de la caméra...</p>
            </div>
          </div>
        )}

        {/* Vidéo plein écran */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`w-full h-full object-cover ${videoReady ? 'opacity-100' : 'opacity-0'}`}
        />

        {/* Canvas overlay pour afficher les marqueurs ArUco détectés */}
        {videoReady && (
          <canvas
            ref={overlayCanvasRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ zIndex: 10 }}
          />
        )}

        {/* Overlay de capture (flash blanc) */}
        {isCapturing && videoReady && (
          <div className="absolute inset-0 bg-white opacity-50 animate-pulse pointer-events-none" />
        )}

        {/* Header overlay */}
        <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/70 to-transparent">
          <div className="flex items-center justify-between">
            <button
              onClick={handleExit}
              className="p-2 rounded-full bg-black/50 text-white"
            >
              <X className="h-6 w-6" />
            </button>

            <div className="flex items-center gap-2">
              {/* Indicateur de détection de formulaire */}
              <div className={`p-2 rounded-full ${formDetected ? 'bg-green-500' : 'bg-red-500/50'}`}>
                {formDetected ? (
                  <FileCheck className="h-5 w-5 text-white" />
                ) : (
                  <FileX className="h-5 w-5 text-white/70" />
                )}
              </div>
              {autoScanEnabled && (
                <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  <Zap className="h-4 w-4 animate-pulse" />
                  Auto
                </div>
              )}
              <span className="px-3 py-1 bg-white/20 text-white rounded-full text-sm font-medium backdrop-blur-sm">
                {scannedForms.length} scanné(s)
              </span>
            </div>
          </div>
        </div>

        {/* Miniatures en bas (scrollable horizontalement) */}
        {scannedForms.length > 0 && (
          <div className="absolute bottom-32 left-0 right-0 px-4">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {scannedForms.slice(-5).map((scan) => (
                <div key={scan.id} className="relative flex-shrink-0">
                  <img
                    src={scan.imageThumbnail}
                    alt="Scan"
                    className="w-16 h-16 object-cover rounded-lg border-2 border-white/50"
                    onClick={() => setShowThumbnails(true)}
                  />
                  <div className="absolute -top-1 -right-1">
                    {scan.uploadStatus === 'uploaded' && (
                      <CheckCircle className="h-4 w-4 text-green-400 bg-black rounded-full" />
                    )}
                    {scan.uploadStatus === 'pending' && (
                      <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    )}
                    {scan.uploadStatus === 'error' && (
                      <XCircle className="h-4 w-4 text-red-400 bg-black rounded-full" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contrôles en bas */}
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent">
          <div className="flex items-center justify-center gap-6">
            {/* Bouton Auto-Scan */}
            <button
              onClick={autoScanEnabled ? stopAutoScan : startAutoScan}
              className={`p-4 rounded-full ${
                autoScanEnabled
                  ? 'bg-orange-500 text-white'
                  : 'bg-white/20 text-white backdrop-blur-sm'
              }`}
            >
              <Zap className="h-6 w-6" />
            </button>

            {/* Bouton Capture principal */}
            <button
              onClick={handleManualCapture}
              disabled={isCapturing}
              className="w-20 h-20 rounded-full border-4 border-white flex items-center justify-center disabled:opacity-50"
            >
              <div className={`w-16 h-16 rounded-full bg-white ${isCapturing ? 'scale-90' : ''} transition-transform`} />
            </button>

            {/* Bouton Valider */}
            <button
              onClick={() => {
                stopCamera();
                navigate(`/batch-validation/${testId}`);
              }}
              disabled={scannedForms.length === 0}
              className={`p-4 rounded-full ${
                scannedForms.length > 0
                  ? 'bg-green-500 text-white'
                  : 'bg-white/20 text-white/50 backdrop-blur-sm'
              }`}
            >
              <CheckCircle className="h-6 w-6" />
            </button>
          </div>

          {/* Labels sous les boutons */}
          <div className="flex items-center justify-center gap-6 mt-2">
            <span className="text-white/70 text-xs w-14 text-center">
              {autoScanEnabled ? 'Stop' : 'Auto'}
            </span>
            <span className="text-white/70 text-xs w-20 text-center">
              Capturer
            </span>
            <span className="text-white/70 text-xs w-14 text-center">
              Valider
            </span>
          </div>
        </div>

        {/* Modal des miniatures */}
        {showThumbnails && (
          <div className="absolute inset-0 bg-black/90 z-10 p-4 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-lg font-semibold">
                Formulaires scannés ({scannedForms.length})
              </h3>
              <button
                onClick={() => setShowThumbnails(false)}
                className="p-2 rounded-full bg-white/20 text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {scannedForms.map((scan) => (
                <div key={scan.id} className="relative">
                  <img
                    src={scan.imageThumbnail}
                    alt="Scan"
                    className="w-full aspect-square object-cover rounded-lg"
                  />
                  <div className="absolute top-1 right-1">
                    {scan.uploadStatus === 'uploaded' && (
                      <CheckCircle className="h-5 w-5 text-green-400 bg-black rounded-full" />
                    )}
                    {scan.uploadStatus === 'pending' && (
                      <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    )}
                    {scan.uploadStatus === 'error' && (
                      <XCircle className="h-5 w-5 text-red-400 bg-black rounded-full" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Mode non-scanning (écran de démarrage)
  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header */}
      <div className="p-4">
        <button
          onClick={() => navigate(`/test-results/${testId}`)}
          className="flex items-center text-white/70 hover:text-white"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Retour
        </button>
      </div>

      {/* Contenu centré */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center mb-6">
          <Camera className="h-12 w-12 text-white/70" />
        </div>

        <h1 className="text-2xl font-bold text-white mb-2 text-center">
          Scanner de formulaires
        </h1>

        <p className="text-white/60 text-center mb-8 max-w-sm">
          Positionnez le formulaire devant la caméra et appuyez sur le bouton de capture
        </p>

        {cameraError && (
          <div className="bg-red-500/20 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-6 max-w-sm text-center">
            <p className="text-sm">{cameraError}</p>
            <p className="text-xs mt-1 text-red-400">Assurez-vous d'avoir autorisé l'accès à la caméra</p>
          </div>
        )}

        <button
          onClick={startCamera}
          className="px-8 py-4 bg-blue-600 text-white rounded-full font-semibold flex items-center gap-3 hover:bg-blue-700 transition"
        >
          <Camera className="h-6 w-6" />
          Démarrer la caméra
        </button>
      </div>

      {/* Scans précédents */}
      {scannedForms.length > 0 && (
        <div className="p-4 bg-white/5">
          <p className="text-white/60 text-sm mb-2">
            {scannedForms.length} formulaire(s) scanné(s)
          </p>
          <button
            onClick={() => navigate(`/batch-validation/${testId}`)}
            className="w-full py-3 bg-green-600 text-white rounded-lg font-semibold"
          >
            Valider les scans
          </button>
        </div>
      )}
    </div>
  );
};

export default CameraScannerPage;
